import { FunctionComponent, useState, useEffect } from "react";
import styled from "styled-components";
import { styles } from "../styles/themes/style";
import DropdownMenu from "../components/dropdown/DropdownMenu";
import DropdownItem from "../components/dropdown/DropdownItem";
import TabsSearch from "../components/TabsSearch";
import Row from "../components/Row";
import Checkbox from "../components/Checkbox";
import Text from "../components/Text";

interface IJobCompanyFilterFragmentProps {
  hasJobChanges?: boolean;
  hasCompanyChanges?: boolean;
  infoTitle: string;
  inputTitle: string;
  icon?: React.ReactNode;
  onJobChangesHandler: (value: boolean) => void;
  onCompanyChangesHandler: (value: boolean) => void;
  onResetHandler: () => void;
}

const JobCompanyFilterFragment: FunctionComponent<IJobCompanyFilterFragmentProps> = ({
  hasJobChanges = false,
  hasCompanyChanges = false,
  infoTitle,
  inputTitle,
  icon,
  onJobChangesHandler,
  onCompanyChangesHandler,
  onResetHandler,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([
    {
      id: "job-changes",
      label: "Job Changes",
      selected: hasJobChanges,
    },
    {
      id: "company-changes", 
      label: "Company Changes",
      selected: hasCompanyChanges,
    }
  ]);

  const dropdownButton = (
    <StyledControlDiv
      numberOfSelectedItems={selectedOptions.filter(option => option.selected).length}
    >
      {icon && <IconSlot>{icon}</IconSlot>}
      <LabelText>{inputTitle}:</LabelText>
      {selectedOptions.some(option => option.selected) ? (
        <ValueText $active>
          {selectedOptions.filter(option => option.selected).length}
          &nbsp;Selected
        </ValueText>
      ) : (
        <ValueText>Show All</ValueText>
      )}
    </StyledControlDiv>
  );

  useEffect(() => {
    setSelectedOptions([
      {
        id: "job-changes",
        label: "Job Changes", 
        selected: hasJobChanges,
      },
      {
        id: "company-changes",
        label: "Company Changes",
        selected: hasCompanyChanges,
      }
    ]);
  }, [hasJobChanges, hasCompanyChanges]);

  const toggleOptionHandler = (event: any, item: any) => {
    event.stopPropagation();
    
    setSelectedOptions(
      selectedOptions.map((option) => {
        if (option.id === item.id) {
          const newSelected = !option.selected;
          if (option.id === "job-changes") {
            onJobChangesHandler(newSelected);
          } else if (option.id === "company-changes") {
            onCompanyChangesHandler(newSelected);
          }
          return { ...option, selected: newSelected };
        }
        return { ...option };
      })
    );
  };

  const resetOptions = () => {
    setSelectedOptions(
      selectedOptions.map((option) => {
        return { ...option, selected: false };
      })
    );
    onResetHandler();
  };

  return (
    <Wrapper>
      <DropdownMenu title={dropdownButton} autoClose="outside" selectStyle>
        <DropdownItem $nonHoverable>
            {selectedOptions.map((item: any) => (
                <DropdownItem
                    key={item.id}
                    onClickHandler={(e: any) => toggleOptionHandler(e, item)}
                >
                    <Row alignItems gap="10px">
                    <Checkbox checked={item.selected} checkboxId={item.id} />
                    <Text>{item.label}</Text>
                    </Row>
                </DropdownItem>
            ))}
        </DropdownItem>
      </DropdownMenu>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  & .actions-dropdown + div {
    width: 100%;
    padding: 16px 15px;
    max-height: 350px;
    overflow: visible;
  }
  & .dropdown-item {
    padding: 0;
  }
`;

const StyledControlDiv = styled.div<{ numberOfSelectedItems: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding-left: 0;
  box-sizing: border-box;
  border-radius: 8px;
  font-family: NunitoSans;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  position: relative;
  user-select: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: ${styles.colors.black300};

  & svg {
    width: 16px;
    height: 16px;
  }
`
const LabelText = styled.span`
  flex-shrink: 0;
  font-weight: 500;
  color: ${styles.colors.black600};
`
const ValueText = styled.span<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black300)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`

export default JobCompanyFilterFragment;